/**
 * OFFIX B2B Shared Carousel & Slider Autoplay
 * Automatically advances every carousel on the site every 3 seconds (3000ms).
 * Handles:
 * (a) Controls-based sliders (hero, testimonials) by triggering next control
 * (b) Native scroll-snap rows (Best Sellers mobile, chips, tracks) via smooth scrollTo
 */
(function() {
  'use strict';

  const AUTOPLAY_INTERVAL = 3000;
  const activeCarousels = new Map();

  /**
   * Check if a carousel or any product card inside it is currently being used
   * (focus in input/slider, exact quantity box open)
   */
  function isCarouselBusy(container) {
    try {
      const activeEl = document.activeElement;
      if (activeEl && container.contains(activeEl)) {
        const tag = activeEl.tagName.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || activeEl.hasAttribute('data-range-slider')) {
          return true;
        }
      }

      // Check for actively open bulk exact quantity container
      const bulkContainers = container.querySelectorAll('[data-bulk-container]');
      for (let i = 0; i < bulkContainers.length; i++) {
        const el = bulkContainers[i];
        if (el && el.style.display !== 'none' && getComputedStyle(el).display !== 'none') {
          return true;
        }
      }
    } catch (e) {
      // Safe fallback
    }
    return false;
  }

  /**
   * Set up shared IntersectionObserver (threshold 0.05)
   */
  let intersectionObserver = null;
  if ('IntersectionObserver' in window) {
    intersectionObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        const state = activeCarousels.get(entry.target);
        if (state) {
          state.isOnScreen = entry.isIntersecting;
        }
      });
    }, { threshold: 0.05 });
  }

  /**
   * Initialize a controls-based carousel (Type A)
   */
  function initControlsCarousel(container, nextBtn) {
    if (!container || !nextBtn || container._carouselAutoplayActive) return;
    if (container.getAttribute('data-autoplay') === 'false') return;

    container._carouselAutoplayActive = true;

    const state = {
      type: 'controls',
      container: container,
      nextBtn: nextBtn,
      timer: null,
      resumeTimer: null,
      isPaused: false,
      isOnScreen: true,
      destroy: null
    };

    function advance() {
      if (document.hidden || !state.isOnScreen || state.isPaused) return;
      if (isCarouselBusy(container)) return;

      try {
        nextBtn.click();
      } catch (err) {
        // Safe fail
      }
    }

    function pause() {
      state.isPaused = true;
      if (state.resumeTimer) {
        clearTimeout(state.resumeTimer);
        state.resumeTimer = null;
      }
    }

    function resumeAfter(delay) {
      if (state.resumeTimer) {
        clearTimeout(state.resumeTimer);
      }
      state.resumeTimer = setTimeout(function() {
        state.isPaused = false;
      }, delay || AUTOPLAY_INTERVAL);
    }

    // Event listeners for user pause/resume
    const onMouseEnter = function() { pause(); };
    const onMouseLeave = function() { resumeAfter(AUTOPLAY_INTERVAL); };
    const onTouchStart = function() { pause(); };
    const onTouchEnd = function() { resumeAfter(AUTOPLAY_INTERVAL); };
    const onFocusIn = function() { pause(); };
    const onFocusOut = function(e) {
      if (!container.contains(e.relatedTarget)) {
        resumeAfter(AUTOPLAY_INTERVAL);
      }
    };

    container.addEventListener('mouseenter', onMouseEnter);
    container.addEventListener('mouseleave', onMouseLeave);
    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('touchend', onTouchEnd, { passive: true });
    container.addEventListener('focusin', onFocusIn);
    container.addEventListener('focusout', onFocusOut);

    if (intersectionObserver) {
      intersectionObserver.observe(container);
    }

    state.timer = setInterval(advance, AUTOPLAY_INTERVAL);

    state.destroy = function() {
      if (state.timer) clearInterval(state.timer);
      if (state.resumeTimer) clearTimeout(state.resumeTimer);
      container.removeEventListener('mouseenter', onMouseEnter);
      container.removeEventListener('mouseleave', onMouseLeave);
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('focusin', onFocusIn);
      container.removeEventListener('focusout', onFocusOut);
      if (intersectionObserver) intersectionObserver.unobserve(container);
      delete container._carouselAutoplayActive;
      activeCarousels.delete(container);
    };

    activeCarousels.set(container, state);
  }

  /**
   * Initialize a native scroll-snap / horizontal overflow carousel (Type B)
   */
  function initScrollSnapCarousel(container) {
    if (!container || container._carouselAutoplayActive) return;
    if (container.getAttribute('data-autoplay') === 'false') return;

    container._carouselAutoplayActive = true;

    const wrap = container.closest('.featured-products-carousel-wrap') || container.parentElement;
    const prevBtn = wrap ? wrap.querySelector('[data-carousel-prev]') : null;
    const nextBtn = wrap ? wrap.querySelector('[data-carousel-next]') : null;
    const fadeLeft = wrap ? wrap.querySelector('.best-sellers-edge-fade--left, .carousel-edge-fade--left') : null;
    const fadeRight = wrap ? wrap.querySelector('.best-sellers-edge-fade--right, .carousel-edge-fade--right') : null;

    const section = container.closest('.section-featured-products') || (wrap ? wrap.parentElement : null);
    const dotsWrap = section ? section.querySelector('[data-carousel-dots], .best-sellers-dots') : null;
    const dots = dotsWrap ? Array.prototype.slice.call(dotsWrap.querySelectorAll('.best-sellers-dot, [data-dot-index]')) : [];
    const swipeHint = section ? section.querySelector('[data-swipe-hint], .best-sellers-swipe-hint') : null;

    const isBestSellers = !!container.closest('.section-featured-products');

    // Desktop: clone first 4 cards after the last for seamless infinite looping
    function setupDesktopClones() {
      if (!isBestSellers || window.innerWidth < 750) return;
      if (container._hasClones) return;

      const originalCards = Array.prototype.slice.call(container.querySelectorAll('.product-card:not(.is-clone)'));
      if (originalCards.length < 4) return;

      container._hasClones = true;
      for (let i = 0; i < 4; i++) {
        const clone = originalCards[i].cloneNode(true);
        clone.classList.add('is-clone');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
        clone.setAttribute('tabindex', '-1');
        clone.style.pointerEvents = 'none';

        clone.removeAttribute('id');
        const withIds = clone.querySelectorAll('[id]');
        for (let j = 0; j < withIds.length; j++) withIds[j].removeAttribute('id');

        const inputs = clone.querySelectorAll('input, select, textarea, button');
        for (let k = 0; k < inputs.length; k++) {
          inputs[k].removeAttribute('name');
          inputs[k].setAttribute('disabled', 'disabled');
          inputs[k].setAttribute('tabindex', '-1');
          inputs[k].setAttribute('aria-hidden', 'true');
        }

        container.appendChild(clone);
      }
    }
    setupDesktopClones();

    let hasInteracted = false;
    function dismissHint() {
      if (!hasInteracted && swipeHint) {
        hasInteracted = true;
        swipeHint.style.opacity = '0';
        setTimeout(function() {
          if (swipeHint) swipeHint.style.display = 'none';
        }, 300);
      }
    }

    function getDesktopStep() {
      const originalCards = Array.prototype.slice.call(container.querySelectorAll('.product-card:not(.is-clone)'));
      if (!originalCards.length) return (container.clientWidth || 280);
      const firstCard = originalCards[0];
      const gap = parseFloat(window.getComputedStyle(container).gap) || 16;
      return firstCard.offsetWidth + gap;
    }

    function updateIndicators() {
      const isDesktop = window.innerWidth >= 750;

      if (isBestSellers && isDesktop) {
        if (prevBtn) {
          prevBtn.style.opacity = '1';
          prevBtn.style.pointerEvents = 'auto';
        }
        if (nextBtn) {
          nextBtn.style.opacity = '1';
          nextBtn.style.pointerEvents = 'auto';
        }
        if (fadeLeft) fadeLeft.style.opacity = '0';
        if (fadeRight) fadeRight.style.opacity = '0';

        if (dots.length > 0) {
          const originalCards = Array.prototype.slice.call(container.querySelectorAll('.product-card:not(.is-clone)'));
          const numOriginals = originalCards.length || 8;
          const step = getDesktopStep();

          let closestIndex = Math.round(container.scrollLeft / step);
          let activeDotIndex = ((closestIndex % numOriginals) + numOriginals) % numOriginals;

          dots.forEach(function(dot, idx) {
            if (idx === activeDotIndex) {
              dot.classList.add('is-active');
              dot.setAttribute('aria-selected', 'true');
            } else {
              dot.classList.remove('is-active');
              dot.setAttribute('aria-selected', 'false');
            }
          });
        }
        return;
      }

      const scrollLeft = container.scrollLeft;
      const clientWidth = container.clientWidth;
      const scrollWidth = container.scrollWidth;

      if (prevBtn) {
        if (scrollLeft <= 5) {
          prevBtn.style.opacity = '0';
          prevBtn.style.pointerEvents = 'none';
        } else {
          prevBtn.style.opacity = '1';
          prevBtn.style.pointerEvents = 'auto';
        }
      }
      if (nextBtn) {
        if (scrollLeft + clientWidth >= scrollWidth - 10) {
          nextBtn.style.opacity = '0';
          nextBtn.style.pointerEvents = 'none';
        } else {
          nextBtn.style.opacity = '1';
          nextBtn.style.pointerEvents = 'auto';
        }
      }

      if (fadeLeft) {
        fadeLeft.style.opacity = scrollLeft > 10 ? '1' : '0';
      }
      if (fadeRight) {
        fadeRight.style.opacity = (scrollLeft + clientWidth < scrollWidth - 10) ? '1' : '0';
      }

      if (dots.length > 0) {
        const children = Array.prototype.slice.call(container.children).filter(function(child) {
          return child.nodeType === 1 && getComputedStyle(child).display !== 'none' && !child.classList.contains('is-clone');
        });
        if (children.length > 0) {
          const containerRect = container.getBoundingClientRect();
          let closestIndex = 0;
          let minDistance = Infinity;

          children.forEach(function(child, idx) {
            const childRect = child.getBoundingClientRect();
            const dist = Math.abs(childRect.left - containerRect.left);
            if (dist < minDistance) {
              minDistance = dist;
              closestIndex = idx;
            }
          });

          dots.forEach(function(dot, idx) {
            if (idx === closestIndex) {
              dot.classList.add('is-active');
              dot.setAttribute('aria-selected', 'true');
            } else {
              dot.classList.remove('is-active');
              dot.setAttribute('aria-selected', 'false');
            }
          });
        }
      }
    }

    const state = {
      type: 'scroll-snap',
      container: container,
      timer: null,
      resumeTimer: null,
      isPaused: false,
      isOnScreen: true,
      destroy: null
    };

    function retreat(isManual) {
      const isDesktop = window.innerWidth >= 750;

      if (isBestSellers && isDesktop && container._hasClones) {
        const originalCards = Array.prototype.slice.call(container.querySelectorAll('.product-card:not(.is-clone)'));
        const numOriginals = originalCards.length || 8;
        const step = getDesktopStep();

        let currentIndex = Math.round(container.scrollLeft / step);

        if (currentIndex <= 0) {
          container.scrollLeft = numOriginals * step;
          currentIndex = numOriginals;
        }

        const prevIndex = currentIndex - 1;
        container.scrollTo({ left: prevIndex * step, behavior: 'smooth' });
        return;
      }

      const children = Array.prototype.slice.call(container.children).filter(function(child) {
        return child.nodeType === 1 && getComputedStyle(child).display !== 'none' && !child.classList.contains('is-clone');
      });
      if (children.length <= 1) return;

      const scrollLeft = container.scrollLeft;
      const containerRect = container.getBoundingClientRect();
      let targetScrollLeft = 0;

      for (let i = children.length - 1; i >= 0; i--) {
        const child = children[i];
        const childRect = child.getBoundingClientRect();
        const relativeLeft = childRect.left - containerRect.left + scrollLeft;
        if (relativeLeft < scrollLeft - 12) {
          targetScrollLeft = relativeLeft;
          break;
        }
      }
      container.scrollTo({ left: Math.max(0, targetScrollLeft), behavior: 'smooth' });
    }

    function advance(isManual) {
      if (!isManual) {
        if (document.hidden || !state.isOnScreen || state.isPaused) return;
        if (container.scrollWidth <= container.clientWidth + 10) return;
        if (isCarouselBusy(container)) return;
      }

      try {
        const isDesktop = window.innerWidth >= 750;

        if (isBestSellers && isDesktop && container._hasClones) {
          const originalCards = Array.prototype.slice.call(container.querySelectorAll('.product-card:not(.is-clone)'));
          const numOriginals = originalCards.length || 8;
          const step = getDesktopStep();

          let currentIndex = Math.round(container.scrollLeft / step);
          if (currentIndex < 0) currentIndex = 0;

          if (currentIndex >= numOriginals) {
            container.scrollLeft = 0;
            currentIndex = 0;
          }

          const nextIndex = currentIndex + 1;
          container.scrollTo({ left: nextIndex * step, behavior: 'smooth' });

          if (nextIndex >= numOriginals) {
            setTimeout(function() {
              container.scrollLeft = 0;
              updateIndicators();
            }, 400);
          }
          return;
        }

        const scrollLeft = container.scrollLeft;
        const clientWidth = container.clientWidth;
        const scrollWidth = container.scrollWidth;

        if (scrollLeft + clientWidth >= scrollWidth - 16) {
          container.scrollTo({ left: 0, behavior: 'smooth' });
          return;
        }

        const children = Array.prototype.slice.call(container.children).filter(function(child) {
          return child.nodeType === 1 && getComputedStyle(child).display !== 'none' && !child.classList.contains('is-clone');
        });

        if (children.length <= 1) return;

        const containerRect = container.getBoundingClientRect();
        let targetScrollLeft = -1;

        for (let i = 0; i < children.length; i++) {
          const child = children[i];
          const childRect = child.getBoundingClientRect();
          const relativeLeft = childRect.left - containerRect.left + scrollLeft;
          if (relativeLeft > scrollLeft + 12) {
            targetScrollLeft = relativeLeft;
            break;
          }
        }

        if (targetScrollLeft > 0) {
          container.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
        } else {
          const cardWidth = children[0].offsetWidth || (clientWidth * 0.78);
          container.scrollTo({ left: scrollLeft + cardWidth + 10, behavior: 'smooth' });
        }
      } catch (err) {
        // Safe fail
      }
    }

    function pause() {
      state.isPaused = true;
      if (state.resumeTimer) {
        clearTimeout(state.resumeTimer);
        state.resumeTimer = null;
      }
    }

    function resumeAfter(delay) {
      if (state.resumeTimer) {
        clearTimeout(state.resumeTimer);
      }
      state.resumeTimer = setTimeout(function() {
        state.isPaused = false;
      }, delay || AUTOPLAY_INTERVAL);
    }

    const onMouseEnter = function() { pause(); };
    const onMouseLeave = function() { resumeAfter(AUTOPLAY_INTERVAL); };
    const onTouchStart = function() {
      dismissHint();
      pause();
    };
    const onTouchEnd = function() { resumeAfter(AUTOPLAY_INTERVAL); };
    const onFocusIn = function() { pause(); };
    const onFocusOut = function(e) {
      if (!container.contains(e.relatedTarget)) {
        resumeAfter(AUTOPLAY_INTERVAL);
      }
    };

    container.addEventListener('mouseenter', onMouseEnter);
    container.addEventListener('mouseleave', onMouseLeave);
    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('touchend', onTouchEnd, { passive: true });
    container.addEventListener('focusin', onFocusIn);
    container.addEventListener('focusout', onFocusOut);

    const onKeyDown = function(e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        pause();
        advance(true);
        dismissHint();
        resumeAfter(AUTOPLAY_INTERVAL);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        pause();
        retreat(true);
        dismissHint();
        resumeAfter(AUTOPLAY_INTERVAL);
      }
    };
    container.addEventListener('keydown', onKeyDown);

    const onScroll = function() {
      dismissHint();
      updateIndicators();
    };
    container.addEventListener('scroll', onScroll, { passive: true });

    const onResize = function() {
      if (isBestSellers && window.innerWidth >= 750) {
        setupDesktopClones();
      }
      updateIndicators();
    };
    window.addEventListener('resize', onResize);

    const onNextClick = function(e) {
      e.preventDefault();
      pause();
      advance(true);
      dismissHint();
      resumeAfter(AUTOPLAY_INTERVAL);
    };

    const onPrevClick = function(e) {
      e.preventDefault();
      pause();
      retreat(true);
      dismissHint();
      resumeAfter(AUTOPLAY_INTERVAL);
    };

    if (nextBtn) nextBtn.addEventListener('click', onNextClick);
    if (prevBtn) prevBtn.addEventListener('click', onPrevClick);

    const dotClickHandlers = [];
    dots.forEach(function(dot, idx) {
      const handler = function(e) {
        e.preventDefault();
        pause();
        dismissHint();
        const isDesktop = window.innerWidth >= 750;
        if (isBestSellers && isDesktop) {
          const step = getDesktopStep();
          container.scrollTo({ left: idx * step, behavior: 'smooth' });
        } else {
          const children = Array.prototype.slice.call(container.children).filter(function(child) {
            return child.nodeType === 1 && getComputedStyle(child).display !== 'none' && !child.classList.contains('is-clone');
          });
          if (children[idx]) {
            const child = children[idx];
            const containerRect = container.getBoundingClientRect();
            const childRect = child.getBoundingClientRect();
            const targetLeft = childRect.left - containerRect.left + container.scrollLeft;
            container.scrollTo({ left: targetLeft, behavior: 'smooth' });
          }
        }
        resumeAfter(AUTOPLAY_INTERVAL);
      };
      dot.addEventListener('click', handler);
      dotClickHandlers.push({ dot: dot, handler: handler });
    });

    // Mouse drag support for desktop
    let isDown = false;
    let startX = 0;
    let scrollStart = 0;
    let isDragging = false;

    const onMouseDown = function(e) {
      if (e.button !== 0) return;
      if (e.target.closest('button, input, a, select, textarea, .product-card__slider-wrap, .product-card__range-slider')) return;
      isDown = true;
      isDragging = false;
      startX = e.pageX;
      scrollStart = container.scrollLeft;
      pause();
    };

    const onMouseMove = function(e) {
      if (!isDown) return;
      const walk = e.pageX - startX;
      if (Math.abs(walk) > 4) {
        isDragging = true;
        container.style.cursor = 'grabbing';
        container.style.userSelect = 'none';
        container.scrollLeft = scrollStart - walk;
      }
    };

    const onMouseUp = function(e) {
      if (!isDown) return;
      isDown = false;
      container.style.cursor = '';
      container.style.userSelect = '';
      if (isDragging) {
        dismissHint();
        setTimeout(updateIndicators, 50);
      }
      resumeAfter(AUTOPLAY_INTERVAL);
    };

    container.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    // Mouse wheel horizontal translation on desktop
    const onWheel = function(e) {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && Math.abs(e.deltaY) > 6) {
        const canScrollLeft = container.scrollLeft > 2;
        const canScrollRight = container.scrollLeft < container.scrollWidth - container.clientWidth - 2;
        if ((e.deltaY > 0 && canScrollRight) || (e.deltaY < 0 && canScrollLeft)) {
          e.preventDefault();
          pause();
          container.scrollLeft += e.deltaY;
          dismissHint();
          resumeAfter(AUTOPLAY_INTERVAL);
        }
      }
    };
    container.addEventListener('wheel', onWheel, { passive: false });

    if (intersectionObserver) {
      intersectionObserver.observe(container);
    }

    state.timer = setInterval(function() {
      advance(false);
    }, AUTOPLAY_INTERVAL);

    // Initial update of indicators
    updateIndicators();

    state.destroy = function() {
      if (state.timer) clearInterval(state.timer);
      if (state.resumeTimer) clearTimeout(state.resumeTimer);
      container.removeEventListener('mouseenter', onMouseEnter);
      container.removeEventListener('mouseleave', onMouseLeave);
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('focusin', onFocusIn);
      container.removeEventListener('focusout', onFocusOut);
      container.removeEventListener('keydown', onKeyDown);
      container.removeEventListener('scroll', onScroll);
      container.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      container.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', onResize);
      if (nextBtn) nextBtn.removeEventListener('click', onNextClick);
      if (prevBtn) prevBtn.removeEventListener('click', onPrevClick);
      dotClickHandlers.forEach(function(item) {
        item.dot.removeEventListener('click', item.handler);
      });
      if (intersectionObserver) intersectionObserver.unobserve(container);
      delete container._carouselAutoplayActive;
      activeCarousels.delete(container);
    };

    activeCarousels.set(container, state);
  }

  /**
   * Scan root for all carousels and attach autoplay
   */
  function scanAndInitCarousels(root) {
    const scope = root || document;

    try {
      // 1. Controls-based: Hero carousel
      const heroCarousels = scope.querySelectorAll('.hero-carousel, [data-hero-carousel]');
      heroCarousels.forEach(function(hero) {
        const nextBtn = hero.querySelector('[data-action="next"], .hero-carousel__arrow--next');
        const slides = hero.querySelectorAll('.hero-carousel__slide');
        if (slides.length > 1 && nextBtn) {
          initControlsCarousel(hero, nextBtn);
        }
      });

      // 2. Controls-based: Testimonials carousel
      const testimonialCarousels = scope.querySelectorAll('[data-testimonials-carousel], .testimonials-carousel');
      testimonialCarousels.forEach(function(testi) {
        const nextBtn = testi.querySelector('[data-carousel-next], .testimonials-carousel__arrow--next');
        const cards = testi.querySelectorAll('[data-testimonial-card], .testimonial-card');
        if (cards.length > 1 && nextBtn) {
          initControlsCarousel(testi, nextBtn);
        }
      });

      // 3. Generic controls-based sliders
      const genericSliders = scope.querySelectorAll('[data-carousel], [data-slider], [data-slideshow]');
      genericSliders.forEach(function(slider) {
        if (slider._carouselAutoplayActive) return;
        const nextBtn = slider.querySelector('[data-carousel-next], [data-action="next"], .carousel-arrow--next, .slider-button--next');
        if (nextBtn) {
          initControlsCarousel(slider, nextBtn);
        }
      });

      // 4. Native scroll-snap rows (Best Sellers mobile carousel, chips track, carousel-track)
      const scrollSnapContainers = scope.querySelectorAll(
        '.section-featured-products .product-grid, ' +
        '.section-featured-products .catalog-products-grid, ' +
        '.collection-category-chips__track, ' +
        '.carousel-track'
      );
      scrollSnapContainers.forEach(function(snapContainer) {
        initScrollSnapCarousel(snapContainer);
      });

      // 5. Any other element with scroll-snap-type or overflow-x auto
      const allElements = scope.querySelectorAll('[class*="carousel"], [class*="slider"], [class*="track"]');
      allElements.forEach(function(el) {
        if (el._carouselAutoplayActive) return;
        const style = window.getComputedStyle(el);
        if (style.overflowX === 'auto' || style.overflowX === 'scroll' || style.scrollSnapType.indexOf('mandatory') !== -1) {
          if (el.children.length > 1) {
            initScrollSnapCarousel(el);
          }
        }
      });

    } catch (err) {
      console.warn('[Carousel Autoplay] Initialization error:', err);
    }
  }

  function destroyCarouselsIn(root) {
    const scope = root || document;
    activeCarousels.forEach(function(state, element) {
      if (scope.contains(element) || scope === element) {
        if (typeof state.destroy === 'function') {
          state.destroy();
        }
      }
    });
  }

  // Document visibility change listener
  document.addEventListener('visibilitychange', function() {
    const isHidden = document.hidden;
    activeCarousels.forEach(function(state) {
      if (isHidden) {
        state.isPaused = true;
      } else {
        state.isPaused = false;
      }
    });
  });

  // Shopify theme editor live-reload events
  document.addEventListener('shopify:section:load', function(e) {
    destroyCarouselsIn(e.target);
    scanAndInitCarousels(e.target);
  });

  document.addEventListener('shopify:section:unload', function(e) {
    destroyCarouselsIn(e.target);
  });

  // Run on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      scanAndInitCarousels(document);
    });
  } else {
    scanAndInitCarousels(document);
  }
})();
